import * as React from 'react';
import {
  asymmetryReportProvider,
  IAsymmetryReportState,
} from '../../../data/providers/reports/asymmetryReportProvider';
import { Entity, EntityType } from '../../../data/types';
import { Column } from '../../layout/Column';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { EPdfMode, isPdf, Pdf } from '../../shared/pdf/Pdf';
import { PdfContainer } from '../../shared/pdf/PdfContainer';
import { ErrorMessage } from '../utils/ErrorMessages';
import { ReportOverview } from '../utils/ReportOverview';
import { useFilename } from '../utils/useFilename';
import { GraphCard } from './GraphCard';
import { AsymmetrySettings } from './settings/Settings';

interface IProps {
  pdfMode?: EPdfMode;
  entity: Entity;
  entityType: EntityType;
  onPdfModeChange: (pdfMode: EPdfMode) => void;
  loading: boolean;
  setLoading: (v: boolean) => void;
}

export const AsymmetryReport = (props: IProps): React.ReactElement => {
  const [reportState, setReportState] = React.useState<IAsymmetryReportState>();
  /**
   * @description subscribe to data provider
   */
  React.useEffect((): (() => void) => {
    props.setLoading(true);
    const subscriber = asymmetryReportProvider(
      props.entityType,
      props.entity
    ).subscribe((state: IAsymmetryReportState): void => {
      setReportState(state);
      props.setLoading(false);
    });
    return (): void => subscriber.unsubscribe();
  }, [props.entity?.id]);

  /**
   * @description make filename for export
   */
  const filename = useFilename(
    'asymmetry',
    props.entity,
    reportState?.timeframe
  );

  return (
    <React.Fragment>
      {reportState?.isValid && !props.loading ? (
        isPdf(props.pdfMode) ? (
          <PdfContainer>
            <Pdf
              onComplete={(): void => null}
              filename={filename}
              title='Asymmetry Report'
              entity={props.entity}
              loading={props.loading}
              reportState={reportState}
              onPdfModeChange={props.onPdfModeChange}
              pdfMode={props.pdfMode}
            >
              <Column>
                <ReportOverview
                  noComparison
                  targetEntity={props.entity}
                  reportName={`Asymmetry Report - ${reportState?.settings?.name}`}
                  isPdf={isPdf(props.pdfMode)}
                />
                <GraphCard pdfMode={props.pdfMode} reportState={reportState} />
              </Column>
            </Pdf>
          </PdfContainer>
        ) : (
          <React.Fragment>
            {!props.loading ? (
              <React.Fragment>
                <ReportOverview
                  noComparison
                  targetEntity={props.entity}
                  reportName={`Asymmetry Report - ${reportState?.settings?.name}`}
                  isPdf={isPdf(props.pdfMode)}
                />
                <GraphCard pdfMode={props.pdfMode} reportState={reportState} />
              </React.Fragment>
            ) : (
              <LinearLoading />
            )}
          </React.Fragment>
        )
      ) : props.loading ? (
        <LinearLoading />
      ) : (
        <Error
          errorCode={reportState?.errorCode}
          setLoading={props.setLoading}
        />
      )}
    </React.Fragment>
  );
};

const Error = ({
  errorCode,
  setLoading,
}: {
  errorCode?: string;
  setLoading: (v: boolean) => void;
}): React.ReactElement => {
  const onSave = (): void => setLoading(true);
  switch (errorCode) {
    case 'missing-template':
      return (
        <ErrorMessage iconName='window' text='Please Select a Template'>
          <AsymmetrySettings
            style={{ marginTop: '10px', maxWidth: 'max-content' }}
            onSave={onSave}
          />
        </ErrorMessage>
      );

    case 'invalid-date-range':
      return (
        <ErrorMessage iconName='date_range' text='Missing Timeframe'>
          <div style={{ marginTop: '10px' }}>
            Please select a timeframe for this report.
          </div>
          <AsymmetrySettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );
    case 'invalid-baseline-date-range':
      return (
        <ErrorMessage iconName='date_range' text='Missing Baseline Timeframe'>
          <div style={{ marginTop: '10px' }}>
            Please select a baseline timeframe for this report.
          </div>
          <AsymmetrySettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );
    case 'insufficient-data':
      return (
        <ErrorMessage iconName='error_outline' text='Not Enough Data'>
          <div style={{ marginTop: '10px' }}>
            There are not enough data points to produce this report. Try
            changing the timeframe in settings
          </div>
          <AsymmetrySettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );

    default:
      return null;
  }
};
